import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import { upsertLink, unwrapLink } from '@platejs/link';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import styles from './LinkMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';

export function LinkMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();
    const [url, setUrl] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    // Selection snapshot taken at popover-open time. Focusing the URL input
    // moves the browser selection out of the contenteditable, so we restore
    // this before applying the link.
    const savedSelectionRef = useRef<any>(null);

    // True when the selection touches a link — `api.some` so a multi-bullet
    // selection (each bullet wrapped in its own link, separated by paragraph
    // breaks) still offers Remove, not just a single-link caret (RECUR-26761).
    // Falls back to `above` for a collapsed caret sitting inside a link.
    const inLink: boolean = useEditorSelector((ed: any) => {
        try {
            if (ed?.api?.some?.({ match: { type: 'a' } })) return true;
            return Boolean(ed?.api?.above?.({ match: { type: 'a' } }));
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 0);
    }, [open]);

    const openPopover = (e: React.MouseEvent) => {
        e.preventDefault();
        // Snapshot the editor selection BEFORE the URL input grabs focus.
        savedSelectionRef.current = (editor as any)?.selection ?? null;
        // Pre-fill with existing URL if cursor is already inside a link.
        try {
            const above = (editor as any)?.api?.above?.({ match: { type: 'a' } });
            if (above?.[0]?.url) setUrl(above[0].url);
        } catch {
            /* noop */
        }
        setOpen((o) => !o);
    };

    const submit = (e?: any) => {
        e?.preventDefault?.();
        const trimmed = url.trim();
        if (!trimmed) {
            setOpen(false);
            return;
        }
        try {
            const ed: any = editor;
            // Restore the selection that was active when the popover opened.
            const saved = savedSelectionRef.current;
            if (saved) ed?.tf?.select?.(saved);
            (upsertLink as any)(ed, { url: trimmed, skipValidation: true });
        } catch {
            /* defensive: editor not ready */
        }
        setUrl('');
        setOpen(false);
    };

    // Remove the link at the (restored) selection, keeping its text. Shown only
    // when the caret is inside a link (RECUR-26761).
    const remove = (e?: any) => {
        e?.preventDefault?.();
        try {
            const ed: any = editor;
            const saved = savedSelectionRef.current;
            if (saved) ed?.tf?.select?.(saved);
            (unwrapLink as any)(ed);
        } catch {
            /* defensive: editor not ready */
        }
        setUrl('');
        setOpen(false);
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, (inLink || open) && toolbarStyles.btnActive)}
                onMouseDown={openPopover}
                title="Insert link"
                aria-label="Insert link"
                aria-haspopup="true"
                aria-expanded={open}
            >
                <InsertLinkIcon fontSize="small" />
            </button>
            {open && (
                <form className={styles.popover} onSubmit={submit} role="dialog" aria-label="Insert link">
                    <input
                        ref={inputRef}
                        type="url"
                        className={styles.input}
                        placeholder="https://example.com"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submit(e);
                        }}
                    />
                    <button type="submit" className={styles.submit}>
                        Apply
                    </button>
                    {inLink && (
                        <button type="button" className={styles.remove} onMouseDown={remove} title="Remove link" aria-label="Remove link">
                            Remove
                        </button>
                    )}
                </form>
            )}
        </div>
    );
}
